import styled, { css } from 'styled-components';
import type {
  HeaderStyledDealerButtonProps,
  HeaderStyledDealerDisplayProps,
} from '../../interfaces/header-styled-components.interfaces';
import { createBreakpointStyles } from '../../services';
import {
  headerStyledDealerContextMobileStyles,
  headerStyledDealerContextStyles,
} from './DealerContextResponsiveStyles';
import { responsiveStyles } from '../../utils/responsiveStyles';
import { getFontStyle, NumbersSize } from '../../utils/theme-utils';
import { IconButton } from '@oneaudi/unified-web-components';

export const HeaderStyledDealerDisplay = styled.div<HeaderStyledDealerDisplayProps>`
  display: none;
  ${({ headerNavigationItemsAmount, theme, isMobileView }): string => {
    // if headerNavigationItemsAmount is greater than 8, then keep at 8
    const itemsAmount = Math.min(headerNavigationItemsAmount, 8);
    return isMobileView
      ? responsiveStyles(
          createBreakpointStyles(itemsAmount, headerStyledDealerContextMobileStyles()),
          theme.breakpoints,
        )
      : responsiveStyles(
          createBreakpointStyles(itemsAmount, headerStyledDealerContextStyles()),
          theme.breakpoints,
        );
  }};
  & ::-webkit-scrollbar {
    display: none; /* hide scrollbar for Chrome, Safari, and Opera */
    width: 0;
  }
  -ms-overflow-style: none; /*  hide scrollbar for Internet Explorer, Edge */
`;

export const HeaderStyledDealerButton = styled.button<HeaderStyledDealerButtonProps>`
  cursor: pointer;
  outline: 0 none;
  border: 0 none;
  padding: var(--one-header-space-s) var(--one-header-space-xl) var(--one-header-space-s) 0;
  margin: 0;
  background: 0 none;
  width: 100%;
  user-select: none;
  display: flex;
  ${({ theme }): string => (theme.type !== 'legacy' ? 'justify-content: space-between;' : '')};
  flex-flow: row nowrap;
  align-items: center;
  &:focus-visible {
    border: 2px solid;
    border-radius: 3px;
    ${({ theme }): string =>
      theme.type !== 'legacy' ? `border-color: ${theme.colors.colorBaseWhite};` : ''};
  }
  & svg {
    color: ${({ theme }): string => (theme.type === 'legacy' ? 'inherit' : theme.colors.icon)};
  }
  ${({ isMobileView }) =>
    isMobileView &&
    css`
      margin-top: 20px;
      padding: calc(var(--one-header-space-xxl) - var(--one-header-space-l) / 2)
        var(--one-header-side-spacing);
      ${({ theme }): string =>
        theme.type === 'legacy'
          ? `border-top: 1px solid ${theme.colors.colorBaseGrey20}`
          : 'border-top: none'};
      ${({ theme }): string =>
        theme.type === 'legacy'
          ? `border-bottom: 1px solid ${theme.colors.colorBaseGrey20}`
          : 'border-top: none'};
    `};
`;

export const HeaderStyledDealerTextWrapper = styled.div`
  ${({ theme }): string => (theme.type !== 'legacy' ? 'display: flex;' : '')};
  ${({ theme }): string => (theme.type !== 'legacy' ? 'justify-content: flex-start' : '')};

  color: var(${({ theme }): string => theme.colors.colorBaseGrey60});
  text-align: left;
  ${({ theme }): string => (theme.type === 'legacy' ? 'flex-grow: 1;' : '')};

  &:hover,
  &:active,
  &:focus {
    color: var(--one-header-color-black);
  }

  &:hover {
    color: var(--one-header-color-black);
  }

  &:active {
    color: var(--one-header-color-black);
  }
`;

export const HeaderStyledDealerLink = styled.a`
  color: var(${({ theme }): string => theme.colors.colorBaseGrey60});
  text-decoration: none;

  &:hover,
  &:active,
  &:focus {
    color: var(--one-header-color-black);
  }

  &:hover {
    color: var(--one-header-color-black);
  }

  &:active {
    color: var(--one-header-color-black);
  }
`;

interface HeaderStyleDealyerLabelAndNameProps {
  isMobileView: boolean;
}

export const HeaderStyledDealerLabel = styled.label<HeaderStyleDealyerLabelAndNameProps>`
  ${({ theme }): string => (theme.type !== 'legacy' ? 'display: none' : 'display: block;')};
  color: ${({ theme }): string => theme.colors.colorBaseGrey60};
  ${({ isMobileView }) =>
    isMobileView ? getFontStyle(NumbersSize.SMALL) : getFontStyle(NumbersSize.XSMALL)};

  span {
    font-size: 12px;
    font-weight: bold;
    @media (min-width: 1440px) {
      font-size: 10px;
      font-weight: normal;
    }
  }
`;

export const HeaderStyledDealerName = styled.span<HeaderStyleDealyerLabelAndNameProps>`
  color: ${({ theme }): string => theme.colors.colorBaseGrey60};
  ${({ isMobileView }) =>
    isMobileView ? getFontStyle(NumbersSize.MEDIUM) : getFontStyle(NumbersSize.SMALL)};
`;

// Alpha styles
export const StyledIconButton = styled(IconButton)`
  flex-direction: row;
  position: relative;
  margin: ${({ theme }): string => theme.spacing.s} ${({ theme }): string => theme.spacing.l} 0 0;

  &:hover {
    button {
      border-color: transparent !important;
      background-color: transparent !important;
    }
  }

  button {
    width: auto;
  }

  & span {
    font-size: ${({ theme }): string => theme.typography.fontSizeS};
    white-space: nowrap;
  }
`;
